import React, { useState, useEffect } from 'react';
import { 
  ChartBarIcon, 
  DocumentTextIcon, 
  TableCellsIcon,
  XMarkIcon,
  CalendarIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CogIcon
} from '@heroicons/react/24/outline';
import * as XLSX from 'xlsx';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from 'recharts';
import api from '../services/api';
import toast from 'react-hot-toast';

export default function Relatorios() {
  const [activeReport, setActiveReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [movimentacoesData, setMovimentacoesData] = useState([]);
  const [estoqueData, setEstoqueData] = useState([]);
  const [categorias, setCategorias] = useState([]);
  
  // Filtros para relatórios
  const [filtros, setFiltros] = useState({
    dataInicio: '',
    dataFim: '',
    categoriaId: '',
    tipo: '',
    estoqueBaixo: false
  });

  useEffect(() => {
    fetchCategorias();
  }, []);

  const fetchCategorias = async () => {
    try {
      const response = await api.get('/categorias?ativo=true');
      setCategorias(response.data);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  };

  const openReport = async (reportType) => {
    setActiveReport(reportType);
    setLoading(true);
    
    try {
      if (reportType === 'dashboard') {
        await fetchDashboardData();
      } else if (reportType === 'movimentacoes') {
        await fetchMovimentacoesData();
      } else if (reportType === 'estoque') {
        await fetchEstoqueData();
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados do relatório');
    } finally {
      setLoading(false);
    }
  };

  const closeReport = () => {
    setActiveReport(null);
    setDashboardData(null);
    setMovimentacoesData([]);
    setEstoqueData([]);
  };

  const fetchDashboardData = async () => {
    try {
      // Preparar parâmetros de filtro para movimentações
      const movParams = new URLSearchParams();
      if (filtros.dataInicio) movParams.append('dataInicio', filtros.dataInicio);
      if (filtros.dataFim) movParams.append('dataFim', filtros.dataFim);
      movParams.append('limit', '1000'); // Aumentar limite para análise completa

      // Preparar parâmetros de filtro para dashboard
      const dashParams = new URLSearchParams();
      if (filtros.dataInicio) dashParams.append('dataInicio', filtros.dataInicio);
      if (filtros.dataFim) dashParams.append('dataFim', filtros.dataFim);

      const [dashboardRes, movimentacoesRes, estoqueRes] = await Promise.all([
        api.get(`/relatorios/dashboard?${dashParams}`),
        api.get(`/movimentacoes?${movParams}`),
        api.get('/itens?ativo=true&limit=10000')
      ]);

      const dashboard = dashboardRes.data;
      const movimentacoes = movimentacoesRes.data.movimentacoes;
      const itens = estoqueRes.data.itens || estoqueRes.data;

      // Dados para gráficos
      const movimentacoesPorTipo = [
        { name: 'Entradas', value: movimentacoes.filter(m => m.tipo === 'ENTRADA').length, fill: '#10B981' },
        { name: 'Saídas', value: movimentacoes.filter(m => m.tipo === 'SAIDA').length, fill: '#EF4444' },
        { name: 'Ajustes', value: movimentacoes.filter(m => m.tipo === 'AJUSTE').length, fill: '#3B82F6' }
      ];

      const movimentacoesPorMes = processarMovimentacoesPorMes(movimentacoes);
      const estoquePorCategoria = processarEstoquePorCategoria(itens);

      setDashboardData({
        ...dashboard,
        movimentacoesPorTipo,
        movimentacoesPorMes,
        estoquePorCategoria
      });
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
      throw error;
    }
  };

  const fetchMovimentacoesData = async () => {
    try {
      const params = new URLSearchParams();
      if (filtros.dataInicio) params.append('dataInicio', filtros.dataInicio);
      if (filtros.dataFim) params.append('dataFim', filtros.dataFim);
      if (filtros.tipo) params.append('tipo', filtros.tipo);

      const response = await api.get(`/movimentacoes?${params}&limit=1000`);
      setMovimentacoesData(response.data.movimentacoes);
    } catch (error) {
      console.error('Erro ao carregar movimentações:', error);
      throw error;
    }
  };

  const fetchEstoqueData = async () => {
    try {
      const params = new URLSearchParams();
      if (filtros.categoriaId) params.append('categoriaId', filtros.categoriaId);
      if (filtros.estoqueBaixo) params.append('estoqueBaixo', 'true');

      const response = await api.get(`/itens?${params}&ativo=true&limit=10000`);
      setEstoqueData(response.data.itens || response.data);
    } catch (error) {
      console.error('Erro ao carregar estoque:', error);
      throw error;
    }
  };

  const processarMovimentacoesPorMes = (movimentacoes) => {
    const meses = {};
    const mesesNomes = [
      'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
      'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'
    ];

    movimentacoes.forEach(mov => {
      const data = new Date(mov.data);
      const mesAno = `${mesesNomes[data.getMonth()]}/${data.getFullYear()}`;
      
      if (!meses[mesAno]) {
        meses[mesAno] = { entrada: 0, saida: 0, ajuste: 0 };
      }
      
      meses[mesAno][mov.tipo.toLowerCase()]++;
    });

    return Object.entries(meses).map(([mes, valores]) => ({
      mes,
      entrada: valores.entrada,
      saida: valores.saida,
      ajuste: valores.ajuste
    }));
  };

  const processarEstoquePorCategoria = (itens) => {
    const categorias = {};
    
    itens.forEach(item => {
      const categoriaNome = item.categoria?.nome || 'Sem categoria';
      if (!categorias[categoriaNome]) {
        categorias[categoriaNome] = { quantidade: 0, valor: 0 };
      }
      categorias[categoriaNome].quantidade += item.estoqueAtual;
      categorias[categoriaNome].valor += item.estoqueAtual * item.precoUnitario;
    });

    return Object.entries(categorias).map(([nome, dados]) => ({
      name: nome,
      quantidade: dados.quantidade,
      valor: dados.valor
    }));
  };

  const aplicarFiltros = async () => {
    setLoading(true);
    try {
      if (activeReport === 'dashboard') {
        await fetchDashboardData();
      } else if (activeReport === 'movimentacoes') {
        await fetchMovimentacoesData();
      } else if (activeReport === 'estoque') {
        await fetchEstoqueData();
      }
    } catch (error) {
      console.error('Erro ao aplicar filtros:', error);
      toast.error('Erro ao aplicar filtros');
    } finally {
      setLoading(false);
    }
  };

  const limparFiltros = async () => {
    setFiltros({
      dataInicio: '',
      dataFim: '',
      categoriaId: '',
      tipo: '',
      estoqueBaixo: false
    });
    
    // Aplicar automaticamente após limpar
    setTimeout(() => {
      aplicarFiltros();
    }, 100);
  };

  const exportarRelatorio = (tipo) => {
    let dados = [];
    let nomeArquivo = '';

    if (tipo === 'movimentacoes') {
      dados = movimentacoesData;
      nomeArquivo = 'relatorio_movimentacoes.xlsx';
      
      // Preparar dados para Excel (movimentações)
      const dadosFormatados = dados.map(mov => ({
        'Data': new Date(mov.data).toLocaleDateString('pt-BR'),
        'Item': mov.item.nome,
        'Código do Item': mov.item.codigo,
        'Tipo': mov.tipo,
        'Quantidade': mov.quantidade,
        'Unidade': mov.item.unidade,
        'Motivo': mov.motivo,
        'Usuário': mov.user.name,
        'Data/Hora Completa': new Date(mov.data).toLocaleString('pt-BR')
      }));

      // Criar workbook e worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(dadosFormatados);
      
      // Configurar largura das colunas
      const colWidths = [
        { wch: 12 }, // Data
        { wch: 30 }, // Item
        { wch: 15 }, // Código do Item
        { wch: 10 }, // Tipo
        { wch: 12 }, // Quantidade
        { wch: 8 },  // Unidade
        { wch: 40 }, // Motivo
        { wch: 20 }, // Usuário
        { wch: 18 }  // Data/Hora Completa
      ];
      ws['!cols'] = colWidths;
      
      // Adicionar worksheet ao workbook
      XLSX.utils.book_append_sheet(wb, ws, "Relatório de Movimentações");
      
      // Download do arquivo Excel
      XLSX.writeFile(wb, nomeArquivo);
      
    } else if (tipo === 'estoque') {
      dados = estoqueData;
      nomeArquivo = 'relatorio_estoque.xlsx';
      
      // Preparar dados para Excel
      const dadosFormatados = dados.map(item => ({
        'Código': item.codigo,
        'Nome': item.nome,
        'Descrição': item.descricao || '',
        'Categoria': item.categoria?.nome || '',
        'Unidade': item.unidade,
        'Estoque Atual': item.estoqueAtual,
        'Estoque Mínimo': item.estoqueMinimo,
        'Preço Unitário (R$)': item.precoUnitario,
        'Valor Total (R$)': item.estoqueAtual * item.precoUnitario,
        'Fornecedor': item.fornecedor || '',
        'Localização': item.localizacao || '',
        'Status': item.estoqueAtual === 0 ? 'Sem estoque' : 
                 item.estoqueAtual <= item.estoqueMinimo ? 'Estoque baixo' : 'Normal',
        'Ativo': item.ativo ? 'Sim' : 'Não'
      }));

      // Criar workbook e worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(dadosFormatados);
      
      // Configurar largura das colunas
      const colWidths = [
        { wch: 12 }, // Código
        { wch: 25 }, // Nome
        { wch: 30 }, // Descrição
        { wch: 15 }, // Categoria
        { wch: 8 },  // Unidade
        { wch: 12 }, // Estoque Atual
        { wch: 12 }, // Estoque Mínimo
        { wch: 15 }, // Preço Unitário
        { wch: 15 }, // Valor Total
        { wch: 20 }, // Fornecedor
        { wch: 15 }, // Localização
        { wch: 12 }, // Status
        { wch: 8 }   // Ativo
      ];
      ws['!cols'] = colWidths;
      
      // Adicionar worksheet ao workbook
      XLSX.utils.book_append_sheet(wb, ws, "Relatório de Estoque");
      
      // Download do arquivo Excel
      XLSX.writeFile(wb, nomeArquivo);
    }
    
    toast.success('Relatório exportado com sucesso!');
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  if (activeReport) {
    return (
      <div className="space-y-6">
        {/* Header do Relatório */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              {activeReport === 'dashboard' && 'Dashboard'}
              {activeReport === 'movimentacoes' && 'Relatório de Movimentações'}
              {activeReport === 'estoque' && 'Relatório de Estoque'}
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              {activeReport === 'dashboard' && 'Visão geral do sistema'}
              {activeReport === 'movimentacoes' && 'Análise detalhada das movimentações'}
              {activeReport === 'estoque' && 'Situação atual do estoque'}
            </p>
          </div>
          <div className="flex space-x-3">
            {(activeReport === 'movimentacoes' || activeReport === 'estoque') && (
              <button
                onClick={() => exportarRelatorio(activeReport)}
                className="btn-secondary"
              >
                <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                Exportar
              </button>
            )}
            <button onClick={closeReport} className="btn-secondary">
              <XMarkIcon className="h-4 w-4 mr-2" />
              Fechar
            </button>
          </div>
        </div>

                 {/* Filtros */}
         {(activeReport === 'dashboard' || activeReport === 'movimentacoes' || activeReport === 'estoque') && (
           <div className="card">
             <div className="card-body">
               <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                 {activeReport === 'dashboard' && (
                   <>
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">
                         Data Início
                       </label>
                       <input
                         type="date"
                         value={filtros.dataInicio}
                         onChange={(e) => setFiltros(prev => ({ ...prev, dataInicio: e.target.value }))}
                         className="input"
                       />
                     </div>
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">
                         Data Fim
                       </label>
                       <input
                         type="date"
                         value={filtros.dataFim}
                         onChange={(e) => setFiltros(prev => ({ ...prev, dataFim: e.target.value }))}
                         className="input"
                       />
                     </div>
                     <div className="col-span-2">
                       <div className="text-sm text-gray-500 bg-blue-50 p-3 rounded-lg">
                         <strong>Dica:</strong> Use os filtros de data para analisar períodos específicos. Deixe em branco para ver todos os dados.
                       </div>
                     </div>
                   </>
                 )}

                 {activeReport === 'movimentacoes' && (
                   <>
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">
                         Data Início
                       </label>
                       <input
                         type="date"
                         value={filtros.dataInicio}
                         onChange={(e) => setFiltros(prev => ({ ...prev, dataInicio: e.target.value }))}
                         className="input"
                       />
                     </div>
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">
                         Data Fim
                       </label>
                       <input
                         type="date"
                         value={filtros.dataFim}
                         onChange={(e) => setFiltros(prev => ({ ...prev, dataFim: e.target.value }))}
                         className="input"
                       />
                     </div>
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">
                         Tipo
                       </label>
                       <select
                         value={filtros.tipo}
                         onChange={(e) => setFiltros(prev => ({ ...prev, tipo: e.target.value }))}
                         className="input"
                       >
                         <option value="">Todos</option>
                         <option value="ENTRADA">Entrada</option>
                         <option value="SAIDA">Saída</option>
                         <option value="AJUSTE">Ajuste</option>
                       </select>
                     </div>
                   </>
                 )}
                 
                 {activeReport === 'estoque' && (
                   <>
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">
                         Categoria
                       </label>
                       <select
                         value={filtros.categoriaId}
                         onChange={(e) => setFiltros(prev => ({ ...prev, categoriaId: e.target.value }))}
                         className="input"
                       >
                         <option value="">Todas</option>
                         {categorias.map((categoria) => (
                           <option key={categoria.id} value={categoria.id}>
                             {categoria.nome}
                           </option>
                         ))}
                       </select>
                     </div>
                     <div className="flex items-end">
                       <label className="flex items-center">
                         <input
                           type="checkbox"
                           checked={filtros.estoqueBaixo}
                           onChange={(e) => setFiltros(prev => ({ ...prev, estoqueBaixo: e.target.checked }))}
                           className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                         />
                         <span className="ml-2 text-sm text-gray-700">Estoque baixo</span>
                       </label>
                     </div>
                   </>
                 )}
                 
                                   <div className="flex items-end space-x-2">
                    <button onClick={aplicarFiltros} className="btn-primary">
                      <FunnelIcon className="h-4 w-4 mr-2" />
                      Aplicar Filtros
                    </button>
                    <button onClick={limparFiltros} className="btn-secondary">
                      <XMarkIcon className="h-4 w-4 mr-2" />
                      Limpar
                    </button>
                  </div>
               </div>
             </div>
           </div>
         )}

        {/* Conteúdo do Relatório */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <div className="space-y-6">
                         {/* Dashboard */}
             {activeReport === 'dashboard' && dashboardData && (
               <div className="space-y-6">
                 {/* Indicador de Período */}
                 {dashboardData.periodo && (filtros.dataInicio || filtros.dataFim) && (
                   <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                     <div className="flex items-center">
                       <CalendarIcon className="h-5 w-5 text-blue-600 mr-2" />
                       <span className="text-blue-800 font-medium">
                         {dashboardData.periodo.descricao}
                       </span>
                     </div>
                   </div>
                 )}

                 {/* Cards de Resumo */}
                 <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                                     <div className="card">
                     <div className="card-body">
                       <div className="text-2xl font-bold text-gray-900">{dashboardData.totalItens}</div>
                       <div className="text-sm text-gray-500">Total de Itens</div>
                     </div>
                   </div>
                   <div className="card">
                     <div className="card-body">
                       <div className="text-2xl font-bold text-gray-900">{dashboardData.itensEstoqueBaixo}</div>
                       <div className="text-sm text-gray-500">Estoque Baixo</div>
                     </div>
                   </div>
                   <div className="card">
                     <div className="card-body">
                       <div className="text-2xl font-bold text-gray-900">{dashboardData.totalMovimentacoes}</div>
                       <div className="text-sm text-gray-500">
                         {(filtros.dataInicio || filtros.dataFim) ? 'Movimentações no Período' : 'Movimentações Hoje'}
                       </div>
                     </div>
                   </div>
                   <div className="card">
                     <div className="card-body">
                       <div className="text-2xl font-bold text-gray-900">R$ {dashboardData.valorTotalEstoque?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
                       <div className="text-sm text-gray-500">Valor Total do Estoque</div>
                     </div>
                   </div>
                 </div>

                 {/* Cards de Movimentações por Tipo */}
                 <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                   <div className="card">
                     <div className="card-body">
                       <div className="flex items-center">
                         <ArrowUpIcon className="h-8 w-8 text-green-600 mr-3" />
                         <div>
                           <div className="text-2xl font-bold text-green-900">{dashboardData.movimentacoesEntrada || 0}</div>
                           <div className="text-sm text-gray-500">Entradas</div>
                         </div>
                       </div>
                     </div>
                   </div>
                   <div className="card">
                     <div className="card-body">
                       <div className="flex items-center">
                         <ArrowDownIcon className="h-8 w-8 text-red-600 mr-3" />
                         <div>
                           <div className="text-2xl font-bold text-red-900">{dashboardData.movimentacoesSaida || 0}</div>
                           <div className="text-sm text-gray-500">Saídas</div>
                         </div>
                       </div>
                     </div>
                   </div>
                   <div className="card">
                     <div className="card-body">
                       <div className="flex items-center">
                         <CogIcon className="h-8 w-8 text-blue-600 mr-3" />
                         <div>
                           <div className="text-2xl font-bold text-blue-900">{dashboardData.movimentacoesAjuste || 0}</div>
                           <div className="text-sm text-gray-500">Ajustes</div>
                         </div>
                       </div>
                     </div>
                   </div>
                </div>

                {/* Gráficos */}
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  {/* Movimentações por Tipo */}
                  <div className="card">
                    <div className="card-body">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Movimentações por Tipo</h3>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={dashboardData.movimentacoesPorTipo}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {dashboardData.movimentacoesPorTipo.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.fill} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Movimentações por Mês */}
                  <div className="card">
                    <div className="card-body">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Movimentações por Mês</h3>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={dashboardData.movimentacoesPorMes}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="mes" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="entrada" fill="#10B981" name="Entradas" />
                          <Bar dataKey="saida" fill="#EF4444" name="Saídas" />
                          <Bar dataKey="ajuste" fill="#3B82F6" name="Ajustes" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>

                {/* Estoque por Categoria */}
                <div className="card">
                  <div className="card-body">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Estoque por Categoria</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={dashboardData.estoquePorCategoria}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="quantidade" fill="#3B82F6" name="Quantidade" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            )}

            {/* Relatório de Movimentações */}
            {activeReport === 'movimentacoes' && (
              <div className="card">
                <div className="card-body">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Movimentações ({movimentacoesData.length} registros)
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Data
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Item
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Tipo
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Quantidade
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Motivo
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Usuário
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {movimentacoesData.map((mov) => (
                          <tr key={mov.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {new Date(mov.data).toLocaleDateString('pt-BR')}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">{mov.item.nome}</div>
                              <div className="text-sm text-gray-500">{mov.item.codigo}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 mr-2">
                                  {mov.tipo === 'ENTRADA' ? (
                                    <ArrowUpIcon className="h-4 w-4 text-green-600" />
                                  ) : mov.tipo === 'AJUSTE' ? (
                                    <CogIcon className="h-4 w-4 text-blue-600" />
                                  ) : (
                                    <ArrowDownIcon className="h-4 w-4 text-red-600" />
                                  )}
                                </div>
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  mov.tipo === 'ENTRADA' 
                                    ? 'bg-green-100 text-green-800'
                                    : mov.tipo === 'SAIDA'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-blue-100 text-blue-800'
                                }`}>
                                  {mov.tipo}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {mov.quantidade} {mov.item.unidade}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900">
                              {mov.motivo}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {mov.user.name}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Relatório de Estoque */}
            {activeReport === 'estoque' && (
              <div className="card">
                <div className="card-body">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Estoque ({estoqueData.length} itens)
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Item
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Categoria
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Estoque Atual
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Estoque Mínimo
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Preço Unitário
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Valor Total
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {estoqueData.map((item) => {
                          const valorTotal = item.estoqueAtual * item.precoUnitario;
                          const status = item.estoqueAtual === 0 ? 'Sem estoque' : 
                                        item.estoqueAtual <= item.estoqueMinimo ? 'Estoque baixo' : 'Normal';
                          const statusClass = item.estoqueAtual === 0 ? 'bg-red-100 text-red-800' :
                                             item.estoqueAtual <= item.estoqueMinimo ? 'bg-yellow-100 text-yellow-800' :
                                             'bg-green-100 text-green-800';
                          
                          return (
                            <tr key={item.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">{item.nome}</div>
                                <div className="text-sm text-gray-500">{item.codigo}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div
                                    className="w-3 h-3 rounded-full mr-2"
                                    style={{ backgroundColor: item.categoria?.cor }}
                                  />
                                  <span className="text-sm text-gray-900">
                                    {item.categoria?.nome}
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {item.estoqueAtual} {item.unidade}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {item.estoqueMinimo} {item.unidade}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                R$ {item.precoUnitario.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                R$ {valorTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusClass}`}>
                                  {status}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Relatórios</h1>
        <p className="mt-1 text-sm text-gray-500">
          Relatórios e análises do sistema
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <ChartBarIcon className="h-8 w-8 text-primary-600" />
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">Dashboard</h3>
                <p className="text-sm text-gray-500">Visão geral do sistema</p>
              </div>
            </div>
            <div className="mt-4">
              <button 
                onClick={() => openReport('dashboard')}
                className="btn-primary w-full"
              >
                <EyeIcon className="h-4 w-4 mr-2" />
                Acessar
              </button>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <DocumentTextIcon className="h-8 w-8 text-success-600" />
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">Movimentações</h3>
                <p className="text-sm text-gray-500">Relatório de movimentações</p>
              </div>
            </div>
            <div className="mt-4">
              <button 
                onClick={() => openReport('movimentacoes')}
                className="btn-primary w-full"
              >
                <EyeIcon className="h-4 w-4 mr-2" />
                Gerar
              </button>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <TableCellsIcon className="h-8 w-8 text-warning-600" />
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">Estoque</h3>
                <p className="text-sm text-gray-500">Relatório de estoque</p>
              </div>
            </div>
            <div className="mt-4">
              <button 
                onClick={() => openReport('estoque')}
                className="btn-primary w-full"
              >
                <EyeIcon className="h-4 w-4 mr-2" />
                Gerar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 